import { TElement } from "@udecode/plate";
import { cx } from "class-variance-authority";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import RichTextContent from "@/components/RichTextContent";
import { Paragraph } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import VideoThumbnail from "@/routes/video/components/VideoThumbnail";
import { generateImageUrl } from "@/utils/image";

import { SendPushNotificationButton } from "./components/SendPushNotificationButton";
import { ToggleArticleStatusButton } from "./components/ToggleArticleStatusButton";
import { articleDetailQuery } from "./queries";

export default function ArticleDetailRoute() {
    const { id } = useParams();
    const [result, reexecuteQuery] = useQuery({
        query: articleDetailQuery,
        variables: {
            id: id!,
        },
    });

    const { data, fetching } = result;

    let articleContent = null;

    switch (data?.article.content.__typename) {
        case "RichTextArticle": {
            articleContent = (
                <Card>
                    <CardContent className="pt-6">
                        <RichTextContent content={data.article.content.content as TElement[]} />
                    </CardContent>
                </Card>
            );
            break;
        }
        case "ExternalUrlArticle": {
            articleContent = (
                <Paragraph>
                    <strong>URL:</strong>{" "}
                    <a target="_blank" href={data.article.content.url}>
                        {data.article.content.url}
                    </a>
                </Paragraph>
            );
            break;
        }
        case "StoryArticle": {
            const imageWidth = 200;
            const imageHeight = imageWidth * (1920 / 1080);

            articleContent = (
                <div>
                    <h3>Story:</h3>
                    <div className="flex flex-row gap-2">
                        {data.article.content.items
                            .sort((a, b) => a.rank - b.rank)
                            .map(story => {
                                if (story.content.__typename === "StoryArticleImage") {
                                    return (
                                        <div>
                                            <img
                                                className={cx()}
                                                src={generateImageUrl({
                                                    imageId: story.content.id,
                                                    width: imageWidth ?? 800,
                                                    height: imageHeight ?? undefined,
                                                })}
                                                alt="Article main image"
                                            />
                                            <p className="gray-500 text-sm">
                                                Duration: {story.content.duration}
                                            </p>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <Media
                                            width={imageWidth}
                                            height={imageHeight}
                                            key={story.content.id}
                                            videoId={story.content.id}
                                        />
                                    );
                                }
                            })}
                    </div>
                </div>
            );
            break;
        }
    }

    let headerImage = null;
    switch (data?.article.content.__typename) {
        case "StoryArticle": {
            const imageWidth = 200;
            const imageHeight = imageWidth * (1920 / 1280);
            headerImage = (
                <div>
                    <img
                        className={cx()}
                        src={generateImageUrl({
                            imageId: data?.article.imageId,
                            width: imageWidth ?? 800,
                            height: imageHeight ?? undefined,
                        })}
                        alt="Article main image"
                    />
                </div>
            );
            break;
        }
        default: {
            headerImage = <Media imageId={data?.article.imageId} videoId={data?.article.videoId} />;
        }
    }

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader
                title={data?.article.title}
                loading={!data && fetching}
                actions={
                    <div className="flex gap-4">
                        {data?.article && data.article.status !== "DRAFT" ? (
                            <SendPushNotificationButton article={data?.article} />
                        ) : null}

                        <ToggleArticleStatusButton
                            articleId={data?.article.id}
                            status={data?.article.status}
                            draftUnavailable={data?.article.pushNotificationSent}
                            onStatusChanged={() => {
                                reexecuteQuery();
                            }}
                        />
                        <Button>
                            <Link to="edit">Edit article</Link>
                        </Button>
                    </div>
                }
            />

            {headerImage}

            {articleContent}
        </div>
    );
}

function Media({
    imageId,
    videoId,
    width,
    height,
    vertical = false,
}: {
    imageId?: string | null;
    videoId?: string | null;
    width?: number;
    height?: number;
    vertical?: boolean;
}) {
    if (videoId != null) {
        return (
            <VideoThumbnail
                width={width}
                height={height}
                alt="Article video thumbnail"
                videoId={videoId}
            />
        );
    }

    if (imageId != null) {
        return (
            <img
                className={cx(
                    `max-w-lg`,
                    vertical ? "aspect-[9/16] w-1/2" : "h-64 w-full",
                    "object-cover"
                )}
                src={generateImageUrl({
                    imageId,
                    width: width ?? 800,
                    height: height ?? undefined,
                })}
                alt="Article main image"
            />
        );
    }

    return null;
}
